import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';

import FooterBoton from '../components/footer-boton';
import { fetchBlogPost, formatDate } from '../services/blogService';
import './home.css';
import './blog-post.css';

const transformFootnotes = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return markdown;
  }

  const lines = markdown.split('\n');
  const skipIndexes = new Set();
  const footnotes = [];

  for (let i = 0; i < lines.length; i += 1) {
    const match = lines[i].match(/^\[\^([^\]]+)\]:\s*(.*)$/);

    if (!match) {
      continue;
    }

    const identifier = match[1];
    let text = match[2] || '';
    skipIndexes.add(i);

    let j = i + 1;
    while (j < lines.length) {
      const nextLine = lines[j];

      if (/^\s{4,}/.test(nextLine)) {
        text = `${text}\n${nextLine.replace(/^\s{4}/, '')}`;
        skipIndexes.add(j);
        j += 1;
        continue;
      }

      if (nextLine.trim() === '') {
        text = `${text}\n`;
        skipIndexes.add(j);
        j += 1;
        continue;
      }

      break;
    }

    footnotes.push({
      identifier,
      text: text.trim(),
    });

    i = j - 1;
  }

  if (!footnotes.length) {
    return markdown;
  }

  const withoutDefinitions = lines
    .filter((_, index) => !skipIndexes.has(index))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trimEnd();

  const withReferences = withoutDefinitions.replace(
    /\[\^([^\]]+)\]/g,
    (_, identifier) => `(*Nota ${identifier}*)`
  );

  const notesSection = [
    '\n\n---\n\n## Notas',
    ...footnotes.map(
      ({ identifier, text }) => `- **Nota ${identifier}:** ${text}`
    ),
  ].join('\n');

  return `${withReferences}${notesSection}`;
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get post metadata from service
        const postData = await fetchBlogPost(slug);
        if (!postData) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        setPost(postData);

        // The content is already parsed and available in the post object
        setContent(transformFootnotes(postData.content));

        setLoading(false);
      } catch (err) {
        setError('Error loading post');
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className='blog-post-container'>
        <div className='blog-post-loading'>
          <p>Cargando artículo...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className='blog-post-container'>
        <div className='blog-post-error'>
          <h1>Artículo no encontrado</h1>
          <p>Lo sentimos, el artículo que buscas no existe.</p>
          <Link to='/blog' className='blog-post-back-link'>
            ← Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='blog-post-container'>
      <Helmet>
        <title>{post.title} | Blog de Boton</title>
        <meta name='description' content={post.excerpt} />
        <meta property='og:title' content={`${post.title} | Blog de Boton`} />
        <meta property='og:description' content={post.excerpt} />
        <meta
          property='og:image'
          content={`https://botonapp.com${post.thumbnail}`}
        />
        <meta
          property='og:url'
          content={`https://botonapp.com/blog/${post.id}`}
        />
        <meta property='og:type' content='article' />

        {/* Article structured data */}
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            image: `https://botonapp.com${post.thumbnail}`,
            author: {
              '@type': 'Person',
              name: post.author,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Boton',
              logo: {
                '@type': 'ImageObject',
                url: 'https://botonapp.com/boton_logo/boton-200h.png',
              },
            },
            datePublished: post.date,
            dateModified: post.date,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://botonapp.com/blog/${post.id}`,
            },
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className='blog-post-header'>
        <div className='blog-post-header-container'>
          <Link to='/' className='blog-post-logo-link'>
            <img
              src='/boton_logo/boton-200h.png'
              alt='Boton Logo'
              className='blog-post-logo'
            />
          </Link>
          <nav className='blog-post-nav'>
            <Link to='/' className='blog-post-nav-link'>
              Inicio
            </Link>
            <Link to='/blog' className='blog-post-nav-link'>
              Blog
            </Link>
            <a
              href='https://botonapp.com/login'
              target='_blank'
              rel='noreferrer noopener'
              className='blog-post-nav-link'
            >
              Log in
            </a>
          </nav>
        </div>
      </header>

      {/* Article */}
      <article className='blog-post-article'>
        <div className='blog-post-article-container'>
          {/* Back to blog */}
          <Link to='/blog' className='blog-post-back-link'>
            ← Volver al blog
          </Link>

          {/* Article header */}
          <header className='blog-post-article-header'>
            <div className='blog-post-meta'>
              <span className='blog-post-date'>{formatDate(post.date)}</span>
              <span className='blog-post-author'>por {post.author}</span>
            </div>
            <h1 className='blog-post-title'>{post.title}</h1>
            <p className='blog-post-excerpt'>{post.excerpt}</p>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className='blog-post-tags'>
                {post.tags.map((tag, index) => (
                  <span key={index} className='blog-post-tag'>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Featured image */}
          <div className='blog-post-featured-image'>
            <img
              src={post.thumbnail}
              alt={post.title}
              className='blog-post-image'
            />
          </div>

          {/* Article content */}
          <div className='blog-post-content'>
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>

          {/* Share section */}
          <div className='blog-post-share'>
            <h3>Comparte este artículo</h3>
            <div className='blog-post-share-buttons'>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  post.title
                )}&url=${encodeURIComponent(
                  `https://botonapp.com/blog/${post.id}`
                )}`}
                target='_blank'
                rel='noreferrer noopener'
                className='blog-post-share-button'
              >
                Twitter
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  `https://botonapp.com/blog/${post.id}`
                )}`}
                target='_blank'
                rel='noreferrer noopener'
                className='blog-post-share-button'
              >
                LinkedIn
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                  `${post.title} - https://botonapp.com/blog/${post.id}`
                )}`}
                target='_blank'
                rel='noreferrer noopener'
                className='blog-post-share-button'
              >
                WhatsApp
              </a>
            </div>
          </div>

          {/* CTA section */}
          <section className='home-demo2'>
            <div className='home-action'>
              <div className='home-heading4'>
                <span className='home-text360 title'>
                  ¿Te gustó este artículo?
                </span>
                <span className='home-text361'>
                  Descubre cómo Boton puede automatizar la contabilidad de tu
                  empresa.
                </span>
                <a
                  href='https://calendly.com/d/cscj-pcy-82x'
                  target='_blank'
                  rel='noreferrer noopener'
                  className='home-link21'
                >
                  <div className='home-get-started9 template-button'>
                    <span className='home-text362'>
                      <span>Agendar Demo</span>
                      <br></br>
                    </span>
                  </div>
                </a>
              </div>
              <div className='home-images'>
                <img
                  alt='image'
                  src='/Images/demo-1200w.png'
                  className='home-image3'
                />
              </div>
            </div>
          </section>
        </div>
      </article>

      <FooterBoton />
    </div>
  );
};

export default BlogPost;
